import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;
const DRAG_THRESHOLD = 5;

/**
 * Starts loading the photo a card gallery shows on hover, while the card is scrolled past.
 *
 * Card slides after the first are `loading="lazy"`, and a slide parked off to the side of a
 * horizontal scroller never intersects the viewport, so its photo was still unfetched at the moment
 * the pointer arrived - verified live: every hover slide reported `complete:false` with no request
 * in resource timing, and a cold card derivative takes 379-450ms from the CDN (the width=1000 cut a
 * product page asks for is edge-warm at 45-74ms; the ~350-500px cuts a card asks for are not).
 * That wait IS the "first hover glitches". Dropping the attribute here starts the fetch while the
 * card scrolls into view instead, costs ~0.1ms (the attribute appears in no selector), and is
 * bounded to the cards a visitor actually reaches - not the whole grid.
 * @param {Element} slideshow
 */
function warmHoverImage(slideshow) {
  if (!slideshow.hasAttribute('glide-nav')) return;

  const slides = slideshow.querySelectorAll('slideshow-slide:not([hidden])');
  slides[1]?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
}

/** Galleries waiting for their seam clone, so the whole page pays one style recalc instead of 44. */
const seamPrimeQueue = new Set();
let seamPrimeScheduled = false;

/**
 * Queues a gallery's seam build and runs every queued one in a SINGLE idle task.
 * @param {Slideshow} slideshow
 */
function scheduleSeamPrime(slideshow) {
  seamPrimeQueue.add(slideshow);
  if (seamPrimeScheduled) return;

  seamPrimeScheduled = true;
  const run = () => {
    seamPrimeScheduled = false;
    const batch = [...seamPrimeQueue];
    seamPrimeQueue.clear();
    for (const gallery of batch) gallery.primeGlideSeam();
  };

  if ('requestIdleCallback' in window) {
    requestIdleCallback(run, { timeout: 2000 });
  } else {
    setTimeout(run, 300);
  }
}

/**
 * Shared viewport observer manager for lazy scroll enablement.
 *
 * Limit the number of compositor layers created by slideshows by only enabling scrolling when the slideshow is in the viewport.
 * Resolves known issues with iOS Safari where too many composition layers will crash the page.
 * When a slideshow is NOT in the viewport, it has overflow: hidden (no compositor layer).
 * When a slideshow enters the viewport, the [in-viewport] attribute is added, enabling scrolling.
 */
class SlideshowViewportObserver {
  /** @type {SlideshowViewportObserver | null} */
  static #instance = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /**
   * Gets the singleton instance
   * @returns {SlideshowViewportObserver}
   */
  static getInstance() {
    if (!this.#instance) {
      this.#instance = new SlideshowViewportObserver();
    }
    return this.#instance;
  }

  /**
   * Registers a slideshow to be observed for viewport visibility
   * @param {Slideshow} slideshow - The slideshow to observe
   */
  observe(slideshow) {
    if (!this.#observer) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const slideshowElement = /** @type {Slideshow} */ (entry.target);
            if (entry.isIntersecting) {
              slideshowElement.setAttribute('in-viewport', '');
              warmHoverImage(slideshowElement);
            } else {
              slideshowElement.removeAttribute('in-viewport');
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    this.#observer.observe(slideshow);
  }

  /**
   * Unregisters a slideshow from viewport observation
   * @param {Slideshow} slideshow - The slideshow to unobserve
   */
  unobserve(slideshow) {
    this.#observer?.unobserve(slideshow);
    slideshow.removeAttribute('in-viewport');
  }
}

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Collection page filtering will Morph slideshow galleries in place, updating
    // the slideshow[initial-slide] and slideshow-slide[hidden] attributes.
    // We need to re-select() the slide after the morph is complete, but not before
    // slideshow-slide elements have their [hidden] attribute updated.
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        // Only select if the component is connected and initialized
        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  async connectedCallback() {
    super.connectedCallback();

    // Register with shared viewport observer for lazy scroll enablement.
    // This prevents iOS Safari crashes caused by too many compositor layers.
    SlideshowViewportObserver.getInstance().observe(this);

    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected) return;
    }

    const slideCount = this.slides?.length || 0;
    slideCount <= 1 ? this.#setupSlideshowWithoutControls() : this.#setupSlideshow();

    if (this.hasAttribute('glide-nav') && slideCount > 1) scheduleSeamPrime(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Unregister from shared viewport observer
    SlideshowViewportObserver.getInstance().unobserve(this);

    if (this.#scroll) {
      const { scroller } = this.refs;
      scroller.removeEventListener('mousedown', this.#handleMouseDown);
      scroller.removeEventListener('pointerdown', this.#cancelGlide);
      scroller.removeEventListener('wheel', this.#cancelGlide);
      scroller.removeEventListener('touchstart', this.#cancelGlide);
      this.#cancelGlide();
      this.#scroll.destroy();
    }

    const slideCount = this.slides?.length || 0;
    if (slideCount > 1) {
      this.removeEventListener('mouseenter', this.suspend);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('pointerenter', this.#handlePointerEnter);
      document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  async select(input, event, options = {}) {
    if (this.#disabled || !this.refs.slides?.length) return;
    if (!this.#scroll) return;

    // Any direct select (swatch preview, morph re-select, restore) supersedes an
    // active glide — hand the scroller back before doing stock work.
    this.#cancelGlide();

    // Store the actual current slide before any mutations
    const currentSlide = this.slides?.[this.current];

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    // Figure out the raw desired index (could be -1 if user is on first slide and clicks prev)
    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides.find((slide) => slide.getAttribute('slide-id') == input.id);

        if (!requestedSlide || !this.slides) return;

        // Force the slide to be revealed if it is hidden
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }

        return this.slides.indexOf(requestedSlide);
      }
    })();

    const { current } = this;
    const { slides } = this;

    // Guard checks: no slides, invalid index, or selecting the same slide
    if (!slides?.length || requestedIndex === undefined || isNaN(requestedIndex)) return;

    const requestedSlideElement = slides?.[requestedIndex];
    if (currentSlide === requestedSlideElement) return;

    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    event?.preventDefault();

    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    // Decide the actual target index (clamp for infinite loop)
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;

    // If jump is more than 1 or we looped, do the placeholder + reorder trick
    if (!instant && !isAdjacentSlide && visibleSlides.length === 1) {
      this.#disabled = true;
      await this.#scroll.finished; // ensure we're not mid-scroll

      const targetSlide = slides[index];
      if (!targetSlide || !currentSlide) return;

      // Create a placeholder in the original DOM position of targetSlide
      const placeholder = document.createElement('slideshow-slide');
      targetSlide.before(placeholder);

      // Decide whether targetSlide goes before or after currentSlide
      // so that we scroll a short distance in the correct direction
      if (requestedIndex < current) {
        currentSlide.before(targetSlide);
      } else {
        currentSlide.after(targetSlide);
      }

      if (current === 0) this.#scroll.to(currentSlide, { instant: true });

      // Once that scroll finishes, restore the DOM
      queueMicrotask(async () => {
        await this.#scroll.finished;
        this.#disabled = false;

        // Restore the slide back to its original position. This triggers a scroll event.
        placeholder.replaceWith(targetSlide);

        // Instantly scroll to the target slide as its position will have changed
        this.#scroll.to(targetSlide, { instant: true });

        // Force Safari to recalculate the timeline state on timeline refresh (after loop)
        requestAnimationFrame(() => {
          this.setAttribute('refreshing-timeline', '');
          requestAnimationFrame(() => {
            this.removeAttribute('refreshing-timeline');
          });
        });
      });
    }

    const slide = slides[index];
    if (!slide) return;

    const previousIndex = this.current;

    slide.setAttribute('aria-hidden', 'false');

    if (this.#scroll) {
      this.#scroll.to(slide, { instant });
    }

    this.current = this.slides?.indexOf(slide) || 0;
    this.#mirrorEdgeState();

    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    // The glide serves ANIMATED steps only — that is the arrows (`on:click="/next"`, no options).
    // Instant callers must not be animated: the hover swap calls next({animate:false}) and the owner
    // wants that photo to appear at once, with the sliding motion reserved for arrow navigation.
    // Routing it through the glide also made every pointerenter run the glide's measurements.
    if ((options?.animate ?? true) && this.#glideStep(1)) return;
    this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    // Mirror of next(): animated steps glide, instant callers jump. See the note above.
    if ((options?.animate ?? true) && this.#glideStep(-1)) return;
    this.select(this.previousIndex, event, options);
  }

  /*
   * ------------------------------------------------------------------ GLIDE ---
   * Button navigation for `glide-nav` galleries (product cards) runs on an OWN
   * requestAnimationFrame animator instead of the browser's smooth scrollTo. Native
   * smooth scrolling gives no honest completion signal, RESTARTS its animation on
   * every retarget (the rapid-click jerks), and fights mandatory scroll-snap — the
   * documented reason scroll engines take the scroll over themselves. Here:
   *
   *  - The position is CONTINUOUS: a click during motion only moves the target; the
   *    frame loop eases toward it, so a burst of clicks is one uninterrupted glide
   *    that lands exactly N frames ahead — never backward, never twitching.
   *  - The track is PERIODIC: one trailing clone of the first slide makes the pixel
   *    content repeat with period P, and the physical scroll position is written
   *    modulo P each frame — crossing the seam is pixel-continuous by construction,
   *    in both directions, at any click rate. (The platform still has no native
   *    cyclic scrolling — Chrome team, developer.chrome.com/blog/carousels-with-css.)
   *  - The final frame writes the EXACT slide edge, then snap is re-enabled — at
   *    rest the gallery can never sit between frames.
   *  - Real input on the scroller (pointerdown / wheel / touchstart) cancels the
   *    glide instantly and hands control to native scrolling + snap; the position
   *    sync stays gated only while the glide itself is alive — no heuristics.
   */

  /** @type {{ vPos: number, vTarget: number, offsets: number[], period: number, base: number, index: number, raf: number, lastT: number } | null} */
  #glide = null;

  /** @type {HTMLElement | null} */
  #seamClone = null;

  /**
   * One logical arrow step. Returns false when glide navigation does not apply
   * (not a glide-nav gallery, reduced motion, too few slides) — callers then fall
   * back to the stock select() path.
   * @param {1 | -1} direction
   * @returns {boolean}
   */
  #glideStep(direction) {
    if (!this.hasAttribute('glide-nav') || prefersReducedMotion()) return false;
    const { scroller } = this.refs;
    const slides = this.slides;
    if (!scroller || !this.#scroll || !slides || slides.length < 2) return false;

    if (!this.#glide) {
      const clone = this.#ensureSeam(slides[0]);
      if (!clone) return false;
      const paddingStart = parseFloat(getComputedStyle(scroller).paddingInlineStart) || 0;
      const first = slides[0].offsetLeft;
      const period = clone.offsetLeft - first;
      if (!(period > 0)) return false;
      const base = first - paddingStart;
      const offsets = slides.map((slide) => slide.offsetLeft - first);
      const index = clamp(this.current ?? 0, 0, slides.length - 1);
      this.#glide = {
        // Start from the REAL position (a gesture may have left the gallery anywhere);
        // the first frames glide onto the grid on the way to the target.
        vPos: scroller.scrollLeft - base,
        vTarget: offsets[index],
        offsets,
        period,
        base,
        index,
        raf: 0,
        lastT: 0,
      };
      this.#scroll.snap = false;
    }

    const glide = this.#glide;
    const count = slides.length;
    const from = glide.index;
    const to = (((from + direction) % count) + count) % count;
    let delta = glide.offsets[to] - glide.offsets[from];
    if (direction > 0 && delta <= 0) delta += glide.period;
    if (direction < 0 && delta >= 0) delta -= glide.period;
    glide.vTarget += delta;
    glide.index = to;

    const slide = slides[to];
    slide.setAttribute('aria-hidden', 'false');
    const previousIndex = this.current;
    this.current = to;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index: to,
        previousIndex,
        userInitiated: true,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );

    if (!glide.raf) glide.raf = requestAnimationFrame(this.#glideFrame);
    return true;
  }

  #glideFrame = (time) => {
    const glide = this.#glide;
    if (!glide) return;
    const dt = glide.lastT ? Math.min((time - glide.lastT) / 1000, 0.05) : 1 / 60;
    glide.lastT = time;

    // Exponential approach: velocity is proportional to remaining distance, so a
    // retarget mid-flight only stretches the same motion — no restart, no jerk.
    const ease = 1 - Math.exp(-dt / 0.12);
    let step = (glide.vTarget - glide.vPos) * ease;
    // A speed floor keeps the tail from crawling forever.
    const floor = 240 * dt;
    const remaining = glide.vTarget - glide.vPos;
    if (Math.abs(step) < floor) step = Math.sign(remaining) * Math.min(floor, Math.abs(remaining));
    glide.vPos += step;

    if (Math.abs(glide.vTarget - glide.vPos) < 0.5) {
      this.#finishGlide();
      return;
    }
    this.#writeGlidePosition();
    glide.raf = requestAnimationFrame(this.#glideFrame);
  };

  #writeGlidePosition() {
    const glide = this.#glide;
    if (!glide) return;
    const phys = ((glide.vPos % glide.period) + glide.period) % glide.period;
    // behavior: 'instant' explicitly — the scroller's CSS says scroll-behavior:
    // smooth, so a plain scrollLeft assignment would itself animate.
    this.refs.scroller.scrollTo({ left: glide.base + phys, behavior: 'instant' });
  }

  #finishGlide() {
    const glide = this.#glide;
    if (!glide) return;
    this.#glide = null;
    const phys = ((glide.vTarget % glide.period) + glide.period) % glide.period;
    this.refs.scroller.scrollTo({ left: glide.base + phys, behavior: 'instant' });
    if (this.#scroll) this.#scroll.snap = true;
    this.#removeSeam();
    this.#updateVisibleSlides();
  }

  /**
   * Real input on the scroller takes over instantly: kill the animation, restore
   * snap, renormalize off the seam clone if the window overlaps it (a jump by the
   * period is pixel-identical — invisible), and drop the clone.
   */
  #cancelGlide = () => {
    const glide = this.#glide;
    if (!glide) return;
    cancelAnimationFrame(glide.raf);
    this.#glide = null;
    if (this.#scroll) this.#scroll.snap = true;
    const { scroller } = this.refs;
    const phys = scroller.scrollLeft - glide.base;
    if (this.#seamClone && phys > glide.period - scroller.clientWidth - 1) {
      scroller.scrollTo({ left: scroller.scrollLeft - glide.period, behavior: 'instant' });
    }
    this.#removeSeam();
  };

  /**
   * The trailing clone of the first REAL slide that makes the track periodic.
   * Rebuilt when the lead slide changes (variant flips); inert and id-less so
   * assistive tech, focus and refs never meet the duplicate.
   * @param {HTMLElement} firstSlide
   * @returns {HTMLElement | null}
   */
  #ensureSeam(firstSlide) {
    const sourceId = firstSlide.getAttribute('slide-id') || '';
    if (this.#seamClone?.isConnected && this.#seamClone.getAttribute('data-loop-clone') === sourceId) {
      // Parked, not deleted (see #removeSeam) — waking it is an attribute write, ~4ms.
      this.#seamClone.removeAttribute('hidden');
      return this.#seamClone;
    }
    this.#seamClone?.remove();
    const clone = /** @type {HTMLElement} */ (firstSlide.cloneNode(true));
    clone.setAttribute('data-loop-clone', sourceId);
    clone.setAttribute('aria-hidden', 'false');
    clone.removeAttribute('hidden');
    clone.setAttribute('inert', '');
    clone.removeAttribute('ref');
    clone.removeAttribute('id');
    for (const el of clone.querySelectorAll('[id], [ref]')) {
      el.removeAttribute('id');
      el.removeAttribute('ref');
    }
    this.refs.scroller.appendChild(clone);
    this.#seamClone = clone;
    return clone;
  }

  /**
   * Parks the seam instead of deleting it.
   *
   * Removing a node is a structural DOM change, and on a collection page EVERY structural change
   * costs a full :has() style recalc - measured on live /collections/auskari?page=3: inserting or
   * removing one node inside a card is 67-84ms, and the same node into <body> is 0.1ms, so it is
   * the card's own rule set doing it. The glide paid that twice per card the pointer crossed: once
   * building the seam on the hover step, once dropping it when select() cancelled the glide on
   * pointerleave. That is exactly why hammering one arrow felt perfect (the glide never settles, so
   * the seam is built once and stays) while sweeping across cards felt like sandpaper.
   * `hidden` takes the clone out of layout - [hidden]:not([reveal]) is display:none, so nothing can
   * scroll, snap or drag onto a duplicate slide, same as when it was absent - for ~4ms instead of ~70.
   */
  #removeSeam() {
    this.#seamClone?.setAttribute('hidden', '');
  }

  /**
   * Builds the parked seam clone ahead of time, off the interaction path.
   *
   * Style invalidation coalesces, so doing this for every gallery on the page inside ONE task costs
   * a single recalc: measured 82.1ms for all 44 cards against 67.4ms for a single insertion. Paid
   * once at idle, it buys every card a seam that is already there when the pointer arrives.
   */
  primeGlideSeam() {
    if (!this.hasAttribute('glide-nav') || prefersReducedMotion()) return;
    if (this.#seamClone?.isConnected) return;

    const slides = this.slides;
    if (!slides || slides.length < 2 || !this.refs.scroller) return;

    this.#ensureSeam(slides[0])?.setAttribute('hidden', '');
  }
  /* -------------------------------------------------------------- END GLIDE --- */

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;

    this.#current = value;

    if (current) current.textContent = `${value + 1}`;

    for (const controls of [thumbnails, dots]) {
      // aria-current (not aria-selected): the dots/thumbnails are <button>s, and aria-selected is not
      // an allowed attribute on role=button (Lighthouse aria-allowed-attr). aria-current is the correct
      // "current item in a set" state; the co-located CSS active styles match [aria-current='true'].
      controls?.forEach((el, i) => el.setAttribute('aria-current', `${i === value}`));
    }

    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  get visibleSlides() {
    return this.#visibleSlides;
  }

  get previousIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current - modifier;
  }

  get nextIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current + modifier;
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }

  /**
   * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
   * @type {boolean}
   */
  #disabled = false;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The Scroller instance that manages scrolling.
   * @type {Scroller}
   */
  #scroll;

  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /**
   * IntersectionObserver for efficient visibility tracking of slides
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Cached visible slides result from IntersectionObserver
   * @type {HTMLElement[]}
   */
  #visibleSlides = [];

  /**
   * Setup the slideshow without controls for zero or one slides
   */
  #setupSlideshowWithoutControls() {
    this.current = 0;
    if (this.hasAttribute('auto-hide-controls')) {
      const { slideshowControls } = this.refs;
      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
      }
    }

    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute('aria-hidden', 'false');
    }

    this.#mirrorEdgeState();
  }

  /**
   * Setup the slideshow with controls for when there are multiple slides
   */
  #setupSlideshow() {
    // Setup IntersectionObserver first for efficient visibility tracking
    this.#setupIntersectionObserver();

    // Setup the scroll instance
    const { scroller } = this.refs;
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
      // `slideshow-slides` is `overflow-x: scroll; scroll-snap-type: x mandatory` unconditionally
      // (snippets/slideshow-styles.liquid) and nothing in the theme overrides it to a vertical
      // scroller — so the axis is a fact, not something to measure. Stating it here keeps
      // getScrollAxis() (four geometry reads) out of every select(), which is where the owner's
      // forced-reflow stack started.
      axis: 'x',
    });

    scroller.addEventListener('mousedown', this.#handleMouseDown);

    if (this.hasAttribute('glide-nav')) {
      // Real input takes over from the glide instantly (spec point: a gesture always wins).
      scroller.addEventListener('pointerdown', this.#cancelGlide, { passive: true });
      scroller.addEventListener('wheel', this.#cancelGlide, { passive: true });
      scroller.addEventListener('touchstart', this.#cancelGlide, { passive: true });
    }

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.current = this.initialSlideIndex;

    // Batch reads and writes to the DOM
    scheduler.schedule(() => {
      let visibleSlidesAmount = 0;
      const initialSlideId = this.initialSlide?.getAttribute('slide-id');

      // Wait for next frame to ensure layout is fully calculated before setting initial scroll position
      // This prevents race conditions on Safari mobile when section_width is 'full-width'
      requestAnimationFrame(() => {
        if (this.initialSlideIndex !== 0 && initialSlideId) {
          this.select({ id: initialSlideId }, undefined, { animate: false });
          visibleSlidesAmount = 1;
        } else {
          visibleSlidesAmount = this.#updateVisibleSlides();
          if (visibleSlidesAmount === 0) {
            this.select(0, undefined, { animate: false });
            visibleSlidesAmount = 1;
          }
        }
      });

      this.#resizeObserver = new ResizeObserver(async () => {
        if (viewTransition.current) await viewTransition.current;

        if (visibleSlidesAmount > 1) {
          this.#updateVisibleSlides();
        }

        if (this.hasAttribute('auto-hide-controls')) {
          this.#updateControlsVisibility();
        }
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
  }

  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    // While the glide animator owns the motion, the position sync stays out of the
    // way — the flag is the animation's own lifetime, not a heuristic.
    if (this.#glide) return;

    const previousIndex = this.#current;
    const index = this.#sync();

    if (index === previousIndex) return;

    const slide = this.slides?.[index];
    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    this.#updateVisibleSlides();
    this.removeAttribute('transitioning');
  };

  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    const { slides } = this;
    if (!slides) return (this.current = 0);

    if (!this.#scroll) return (this.current = 0);

    const visibleSlides = this.visibleSlides;

    if (!visibleSlides.length) return this.current;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = visibleSlides.map((slide) => center(slide, axis));
    const referencePoint = visibleSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];

    if (!closestVisibleSlide) return (this.current = 0);

    const index = slides.indexOf(closestVisibleSlide);

    return (this.current = index);
  };

  #dragging = false;

  /**
   * Handles the 'mousedown' event to start dragging slides.
   * @param {MouseEvent} event - The mousedown event.
   */
  #handleMouseDown = (event) => {
    const { slides } = this;

    if (event.button !== 0) return;
    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging) return;

    // Check if the event target is within a 3D model interactive element
    // This prevents the slideshow from capturing drag events when interacting with 3D models
    if (event.target.closest('model-viewer')) {
      return;
    }

    event.preventDefault();
    // Store initial position but don't start handling yet
    const { axis } = this.#scroll;
    const startPosition = event[axis];

    const controller = new AbortController();
    const { signal } = controller;
    const clickController = new AbortController();
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;

    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} event - The pointermove event.
     */
    const onPointerMove = (event) => {
      const current = event[axis];
      const initialDelta = startPosition - current;

      if (!moved && (!initialDelta || Math.abs(initialDelta) < DRAG_THRESHOLD)) return;

      if (!moved) {
        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        // Check if the current slideshow should handle this drag
        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = (movingRight && this.atStart) || (movingLeft && this.atEnd);

        // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        moved = true;
        this.#dragging = true;
        this.setPointerCapture(event.pointerId);

        // Prevent clicks once the user starts dragging
        document.addEventListener('click', preventDefault, { once: true, signal: clickController.signal });

        this.pause();
        this.setAttribute('dragging', '');
        this.#scroll.snap = false;
      }

      // Stop the event from bubbling up to parent slideshow components
      event.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta = performance.now() - startTime;
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      distanceTravelled += Math.abs(delta);

      this.#scroll.by(delta, { instant: true });
    };

    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} event - The pointerup event.
     */
    const onPointerUp = async (event) => {
      controller.abort();
      if (event.type !== 'pointerup') clickController.abort();
      this.#dragging = false;

      if (!moved) return;

      const { current, slides } = this;
      const { scroller } = this.refs;

      if (!slides?.length || !scroller) return;

      const direction = Math.sign(velocity);
      const next = this.#sync();

      const modifier = current !== next || Math.abs(velocity) < 10 || distanceTravelled < 10 ? 0 : direction;
      const newIndex = clamp(next + modifier, 0, slides.length - 1);

      const newSlide = slides[newIndex];
      const currentIndex = this.current;

      if (!newSlide) throw new Error(`Slide not found at index ${newIndex}`);

      this.#scroll.to(newSlide);

      this.removeAttribute('dragging');
      this.releasePointerCapture(event.pointerId);

      this.#centerSelectedThumbnail(newIndex);

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id'),
        })
      );

      this.current = newIndex;

      await this.#scroll.finished;

      // It's possible that the user started dragging again before the scroll finished
      if (this.#dragging) return;

      this.#scroll.snap = true;
      this.resume();
    };

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    /**
     * pointerDown calls onPointerUp to fix an issue where the first tap-and-drag
     * on the zoom dialog is captured by the pointerMove/pointerUp listeners,
     * sometimes causing the slideshow to change slides unexpectedly
     */
    document.addEventListener('pointerdown', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };

  get slides() {
    return this.refs.slides?.filter(
      (slide) => (!slide.hasAttribute('hidden') || slide.hasAttribute('reveal')) && !slide.hasAttribute('data-loop-clone')
    );
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;

    return parseInt(initialSlide, 10);
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller, slideshowControls } = this.refs;

    if (!(slideshowControls instanceof HTMLElement)) return;

    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  /**
   * Setup IntersectionObserver for efficient visibility tracking of slides
   */
  #setupIntersectionObserver() {
    const { slides, scroller } = this.refs;
    if (!slides?.length) return;

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const allEntries = [
          ...entries,
          ...(this.#intersectionObserver ? this.#intersectionObserver.takeRecords() : []),
        ];

        for (const entry of allEntries) {
          const slide = /** @type {HTMLElement} */ (entry.target);
          const isCurrentlyVisible = this.#visibleSlides.includes(slide);
          const shouldBeVisible = entry.intersectionRatio >= SLIDE_VISIBLITY_THRESHOLD;

          if (shouldBeVisible && !isCurrentlyVisible) {
            this.#visibleSlides.push(slide);
          } else if (!shouldBeVisible && isCurrentlyVisible) {
            const index = this.#visibleSlides.indexOf(slide);
            if (index > -1) {
              this.#visibleSlides.splice(index, 1);
            }
          }
        }

        this.#visibleSlides.sort((a, b) => slides.indexOf(a) - slides.indexOf(b));
        this.#updateVisibleSlides();
      },
      {
        root: scroller,
        threshold: SLIDE_VISIBLITY_THRESHOLD,
        // Add small margin to account for sub-pixel rendering
        rootMargin: '1px',
      }
    );

    // Observe all slides - observer will fire initial callback asynchronously
    slides.forEach((slide) => {
      this.#intersectionObserver?.observe(slide);
    });
  }

  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedThumbnail(index, behavior = 'smooth') {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) return;

    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;

    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  /**
   * Mirrors "the first / last slide is on screen" onto the component as `at-start` / `at-end`.
   *
   * sections/slideshow.liquid used to ask exactly this with
   * `:has(slideshow-slide:first-child:not([aria-hidden='true']))`. Any `:has()` whose argument
   * names `aria-hidden` makes EVERY aria-hidden write in the document pay a full style recalc:
   * measured on live /collections/auskari?page=3, one write cost 71-90ms while such rules existed
   * and 3.1-3.3ms once they were all gone. A card swatch hover writes aria-hidden on every slide of
   * the card, twice per swatch, which is where the "photo changes once a second" came from.
   * Only the hint layouts style their arrows from this state, so nothing else pays for the mirror.
   */
  #mirrorEdgeState() {
    if (!this.className.includes('slideshow--with-hints')) return;

    const slides = this.refs.slides;
    if (!slides?.length) return;

    this.toggleAttribute('at-start', slides[0]?.getAttribute('aria-hidden') !== 'true');
    this.toggleAttribute('at-end', slides[slides.length - 1]?.getAttribute('aria-hidden') !== 'true');
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;

    const visibleSlides = this.visibleSlides;

    // If the IntersectionObserver reports zero visible slides, then the slideshow has intersected, but no slides meet the visibility threshold.
    // Probably that the slideshow's host is mid-animation or zero-layout, like in an animating modal.
    // Don't stamp aria-hidden="true" on every slide. Set the current slide to the first visible slide.
    if (visibleSlides.length === 0) return 0;

    // Batch writes to the DOM
    scheduler.schedule(() => {
      // Update aria-hidden based on visibility. ROTAS69: only on change — setAttribute with the same
      // value is still a DOM mutation (every MutationObserver, Clarity's session recording included,
      // gets a record) and runs this on every scroll of every card slideshow in view: 54-197 such
      // writes per 30 s of scrolling, none of them a real change (01.11.2026).
      slides.forEach((slide) => {
        const hidden = `${!visibleSlides.includes(slide)}`;
        if (slide.getAttribute('aria-hidden') !== hidden) slide.setAttribute('aria-hidden', hidden);
      });

      this.#mirrorEdgeState();
    });

    return visibleSlides.length;
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}
